/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FeatureName,
  LIST_ASSETS,
  PermissionValues,
  resolveColumnsForWorkspace,
  useAssetListFilter,
  useAssetListOrder,
  useCurrentAccount,
  useCurrentWorkspace,
  useIsFeatureEnabled,
  useSetAssetListOrder,
} from '@gripp/shared-logic';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteDataGrid, SelectionStatus } from '../infiniteDataGrid';
import { AssetsListOld } from './assetsList-old';
import { useColumns } from './useColumns';

export type AssetListProps = {
  onAssetClick: any;
  paddingBottom?: number;
  onAssetsCountChange?: (count: number) => void;
  updateRowsSelected?: (rows: Array<any>) => void;
  preserveScrollKey?: string;
  addToGroupsComponent?: React.ReactNode;
  isRowSelected?: (row: any) => boolean;
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
  onDataLoaded?: (data: any) => void;
  customEmptyState?: React.ReactNode;
  routinesButton?: (data: any) => React.ReactElement;
  addonsButton?: (data: any) => React.ReactElement;
  alertsButton?: (data: {
    alertsCount: number;
    assetId: string;
    name: string;
  }) => React.ReactElement;
  selectedAssetId?: string;
};

export const AssetsList = ({
  onAssetClick,
  paddingBottom,
  onAssetsCountChange,
  updateRowsSelected,
  preserveScrollKey,
  addToGroupsComponent,
  isRowSelected,
  selectionStatus,
  onSelectionStatusChange,
  onDataLoaded,
  customEmptyState,
  routinesButton,
  addonsButton,
  alertsButton,
  selectedAssetId,
}: AssetListProps) => {
  // TODO: Crop Circle Redesign: Remove after feature is fully rolled out
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  // TODO: Crop Circle Redesign: Remove old AssetsList after feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return (
      <AssetsListOld
        onAssetClick={onAssetClick}
        paddingBottom={paddingBottom}
        onAssetsCountChange={onAssetsCountChange}
        updateRowsSelected={updateRowsSelected}
        preserveScrollKey={preserveScrollKey}
        addToGroupsComponent={addToGroupsComponent}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={onSelectionStatusChange}
        onDataLoaded={onDataLoaded}
        customEmptyState={customEmptyState}
        routinesButton={routinesButton}
        addonsButton={addonsButton}
        alertsButton={alertsButton}
      />
    );
  }

  // TODO: Crop Circle Redesign: Remove to AssetsListBase after feature is fully rolled out
  return (
    <AssetsListBase
      onAssetClick={onAssetClick}
      paddingBottom={paddingBottom}
      onAssetsCountChange={onAssetsCountChange}
      updateRowsSelected={updateRowsSelected}
      preserveScrollKey={preserveScrollKey}
      addToGroupsComponent={addToGroupsComponent}
      isRowSelected={isRowSelected}
      selectionStatus={selectionStatus}
      onSelectionStatusChange={onSelectionStatusChange}
      onDataLoaded={onDataLoaded}
      customEmptyState={customEmptyState}
      routinesButton={routinesButton}
      addonsButton={addonsButton}
      selectedAssetId={selectedAssetId}
      alertsButton={alertsButton}
    />
  );
};

const AssetsListBase = ({
  onAssetClick,
  paddingBottom,
  onAssetsCountChange,
  updateRowsSelected,
  preserveScrollKey,
  addToGroupsComponent,
  isRowSelected,
  selectionStatus,
  onSelectionStatusChange,
  onDataLoaded,
  customEmptyState,
  routinesButton,
  addonsButton,
  alertsButton,
  selectedAssetId,
}: AssetListProps) => {
  const { t } = useTranslation();
  const lastNavigationRef = useRef(0);

  const { data } = useAssetListFilter();
  const { data: orderData } = useAssetListOrder();
  const { mutateAsync: setOrder } = useSetAssetListOrder();
  const workspace = useCurrentWorkspace(true);
  const { account } = useCurrentAccount();
  const showImpersonateWorkspace = useMemo(
    () => !!(account && account.hasPermission(PermissionValues.globalAdmin)),
    [account]
  );

  const setOrderData = useCallback(
    (order: any) => {
      setOrder(order);
    },
    [setOrder]
  );

  const onRowClick = (asset: any) => {
    const now = Date.now();
    if (now - lastNavigationRef.current < 500) return;
    lastNavigationRef.current = now;
    onAssetClick(asset);
  };

  const filter = useMemo(() => {
    if (!data || workspace === undefined) return undefined;

    const f = { ...data };
    f.workspaceId = workspace?.id;
    if (f.keyword === '') delete f.keyword;
    if (f.groups?.length === 0) delete f.groups;

    if (f.columns) {
      f.columns = resolveColumnsForWorkspace(f.columns, workspace?.type);
    }

    return f;
  }, [data, workspace]);

  const { columns, exportColumns } = useColumns(
    showImpersonateWorkspace,
    filter,
    routinesButton,
    addonsButton,
    alertsButton
  ) as {
    columns: ColumnDef<any, any>[];
    exportColumns: ColumnDef<any, any>[];
  };

  return !filter ? null : (
    <InfiniteDataGrid
      modelName="asset"
      query={LIST_ASSETS}
      orderBy={orderData}
      filter={filter}
      pageSize={50}
      columns={columns}
      rowTestIdPrefix="asset-row"
      onRowClick={onRowClick}
      paddingBottom={paddingBottom as number}
      textEmpty={customEmptyState || t('assets.textEmpty')}
      onItemsCountChange={onAssetsCountChange}
      dataPrefix="asset.listAssets"
      onOrderByChange={setOrderData}
      updateRowsSelected={workspace ? updateRowsSelected : undefined}
      isRowSelected={isRowSelected}
      enableScrollRefresh={true}
      preserveScrollKey={preserveScrollKey}
      showScrollIndicator={true}
      headerContent={addToGroupsComponent}
      exportColumns={exportColumns}
      exportFileName="assets"
      selectionStatus={selectionStatus}
      onSelectionStatusChange={onSelectionStatusChange}
      onDataLoaded={onDataLoaded}
      highlightSelectedRow={true}
      selectedRowId={selectedAssetId}
    />
  );
};
